import { observable, action, computed, makeAutoObservable } from 'mobx';
import {
  IVideoData,
  IPlayerData,
  ISetVideoData,
  ISetPlayerData,
} from '@interfaces/video.interface';
import { VideoJsPlayer } from 'video.js';

export interface IVideoStore {
  id: string | '';
  playerRef: VideoJsPlayer | null;
  videoNode: HTMLVideoElement | null;
  videoData: IVideoData;
  playerData: IPlayerData;

  setPlayerRef: (player: VideoJsPlayer) => void;
  setVideoNode: (node: HTMLVideoElement) => void;
  setVideoData: (data: ISetVideoData) => void;
  setPlayerData: (data: ISetPlayerData) => void;
  resetStore: () => void;
  onPlayerReady: boolean;
  videoDuration: number;
  currentProgress: number;
}

const initialState = {
  id: '',
  playerRef: null,
  videoNode: null,
  videoData: {
    videoId: '',
    title: '',
    description: '',
    like: 0,
    dislike: 0,
    duration: 0,
    view: 0,
    playCount: 0,
    createdAt: '',
    updatedAt: '',
    lastPublishedAt: '',
    channelId: '',
    pose: {
      chapters: [],
      time: 0,
      fps: 0,
      createdAt: '',
      updatedAt: '',
      state: 'pending',
    },
    jobs: {
      poseEstimation: 'pending',
      transcoding: 'pending',
    },
    tags: [],
    supportedQualities: [],
    transcodings: [],
    thumbnails: [],
    previews: [],
    categories: [],
    type: 'pose',
    published: false,
    apiVersion: 'v1',
  } as IVideoData,
  playerData: {
    currentTime: 0,
    duration: 0,
    paused: true,
    range: null,
  } as IPlayerData,
};

class VideoStore implements IVideoStore {
  @observable
  id = initialState.id;

  playerRef: VideoJsPlayer | null = null;

  @observable
  videoNode: HTMLVideoElement | null = initialState.videoNode;

  @observable
  videoData: IVideoData = initialState.videoData;

  @observable
  playerData: IPlayerData = initialState.playerData;

  constructor() {
    makeAutoObservable(this);
  }

  @action
  setPlayerRef = (player: VideoJsPlayer) => {
    this.playerRef = player;
  };

  @action
  setVideoNode = (node: HTMLVideoElement) => {
    this.videoNode = node;
  };

  @action
  setVideoData = (data: ISetVideoData) => {
    this.videoData = { ...this.videoData, ...data };
  };

  @action
  setPlayerData = (data: ISetPlayerData) => {
    this.playerData = { ...this.playerData, ...data };
  };

  @action
  resetStore = () => {
    this.id = initialState.id;
    this.videoNode = initialState.videoNode;
    this.videoData = initialState.videoData;
    this.playerData = initialState.playerData;
  };

  @computed
  get onPlayerReady() {
    return this.playerData.duration > 0;
  }

  @computed
  get videoDuration() {
    return this.playerData.duration;
  }

  @computed
  get currentProgress() {
    const { currentTime = 0 } = this.playerData;
    const { duration = 0 } = this.playerData;

    return currentTime / duration;
  }
}

export default new VideoStore();
